'use client';

import { Button, Filter } from '@sprd/sprd-component-kit/v2';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useState } from 'react';
import { useTranslations } from 'next-intl';
import { NestedCategoryFilter } from '@/types/assortment';
import { FilterDrawer } from '@/components/Filters/FilterDrawer';
import { ActiveListFilter, ListFacetsAndCategories } from '@/components/Filters/listFilter';
import { useRouter } from '@/i18n/navigation';
import { FEATURE_SUSTAINABLE_ID } from '@/utility/constants';
import { getListPageSlugByFilter } from '@/utility/listPages';
import { Locale } from '@/utility/locale';
import { post } from '@/utility/requestHelpers/clientRequestHelpers';
import { sanitizeQuery } from '@/utility/urls';

type CYOFilterDrawerProps = {
  shopId: string;
  embroideryShopId: string;
  locale: Locale;
  filter: ListFacetsAndCategories;
  activeFilter: ActiveListFilter<AssortmentListPageSort>;
  categories: NestedCategoryFilter[];
};

export const CYOFilterDrawer = ({
  shopId,
  embroideryShopId,
  locale,
  filter: initialFilter,
  activeFilter: initialActiveFilter,
  categories,
}: CYOFilterDrawerProps) => {
  const t = useTranslations();

  const router = useRouter();
  const [open, setOpen] = useState<boolean>(false);
  const [activeFilter, setActiveFilter] = useState<ActiveListFilter>();
  const { data: filter } = useQuery({
    queryKey: ['cyo-filter', shopId, embroideryShopId, locale, activeFilter],
    queryFn: async () => {
      const filter = activeFilter;
      const { facets } = await post<{ facets: ListFacetsAndCategories }>(
        '/internal/api/combinedAssortment',
        {
          shopId,
          embroideryShopId,
          locale,
          filter: {
            category: filter?.category,
            sizeGroups: filter?.size,
            appearanceGroups: filter?.appearance,
            labels: {
              FEATURE: filter?.sustainable ? [FEATURE_SUSTAINABLE_ID] : undefined,
              GENDER: filter?.department ? [filter?.department] : undefined,
              BRAND: filter?.brand,
              MATERIALS: filter?.materials ? [filter?.materials] : undefined,
              FIT: filter?.fit
                ? Array.isArray(filter.fit)
                  ? filter.fit
                  : [filter.fit]
                : undefined,
              NECKLINE: filter?.neckline
                ? Array.isArray(filter.neckline)
                  ? filter.neckline
                  : [filter.neckline]
                : undefined,
              SLEEVE: filter?.sleeve
                ? Array.isArray(filter.sleeve)
                  ? filter.sleeve
                  : [filter.sleeve]
                : undefined,
            },
            maxPrice: filter?.maxPrice,
            embroidery: filter?.embroidery,
          },
        }
      );

      return { ...facets, categories };
    },
    enabled: !!activeFilter,
  });

  const applyFilter = useCallback(
    (newFilter: Partial<ListFacetsAndCategories>, newActiveFilter: ActiveListFilter) => {
      const slug = getListPageSlugByFilter(newFilter, newActiveFilter);

      setOpen(false);
      router.push(
        {
          pathname: '/cyo/assortment/list/[[...slug]]',
          params: { slug },
          // We don't want to include department and category in the query
          query: sanitizeQuery({ ...newActiveFilter, category: undefined, department: undefined }),
        },
        { scroll: false }
      );
    },
    [router]
  );

  const {
    size: selectedSize,
    appearance: selectedAppearance,
    fit: selectedFit,
    brand: selectedBrand,
    sleeve: selectedSleeve,
    neckline: selectedNeckline,
    embroidery: embroiderySelected,
    department: selectedDepartment,
    maxPrice: selectedMaxPrice,
  } = initialActiveFilter ?? {};

  const selectedFiltersCount = [
    selectedSize,
    selectedAppearance,
    selectedFit,
    selectedNeckline,
    selectedBrand,
    selectedSleeve,
    embroiderySelected,
    selectedMaxPrice,
    selectedDepartment,
  ].filter(Boolean).length;

  const currentFilter = filter ?? initialFilter;
  const currentActiveFilter = activeFilter ?? initialActiveFilter;

  return (
    <>
      <Button
        className="border-none text-sm font-semibold"
        variant="invert"
        onClick={() => setOpen(true)}
        startIcon={<Filter className="size-6" />}
        endIcon={
          selectedFiltersCount && (
            <span className="inline-flex size-4 items-center justify-center bg-black text-[10px] text-white">
              {selectedFiltersCount}
            </span>
          )
        }
      >
        {t('filter.addFilter')}
      </Button>
      <FilterDrawer
        open={open}
        filter={currentFilter}
        activeFilter={currentActiveFilter}
        onFilterUpdate={(nextActiveFilter) => setActiveFilter(nextActiveFilter)}
        onApplyFilter={applyFilter}
      />
    </>
  );
};
